import { useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Shield, Copy, Check } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Card, Button, OtpInput } from '@/components/ui';
import { useAuthStore } from '@/stores';
import { useToast } from '@/components/ui/Toast';
import {
  generateMfaEnrollment,
  verifyTotpCode,
  enableMfa,
  getUserProfile,
  type MfaEnrollmentData,
} from '@perfin/shared';

type SetupStep = 'scanning' | 'verifying' | 'backup-codes';

export function MfaSetupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { pendingUser, mfaSetupRequired, cancelMfa } = useAuthStore();
  const toast = useToast();

  // Preserve redirect parameter from signup flow (e.g., /invite/{token})
  const redirectUrl = searchParams.get('redirect') || '/';

  const [step, setStep] = useState<SetupStep>('scanning');
  const [enrollmentData] = useState<MfaEnrollmentData | null>(() => {
    // Generate enrollment data on mount if we have a pending user
    if (pendingUser?.email) {
      return generateMfaEnrollment(pendingUser.email);
    }
    return null;
  });
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationError, setVerificationError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [copiedBackupCodes, setCopiedBackupCodes] = useState(false);

  // Track navigation to prevent early return from firing during redirect
  const isNavigatingRef = useRef(false);

  // Redirect if no pending setup (but not if we're navigating away)
  if (!mfaSetupRequired || !pendingUser || !enrollmentData) {
    // If we're navigating away after completion, render nothing to prevent flash
    if (isNavigatingRef.current) {
      return null;
    }
    return (
      <Card className="max-w-md mx-auto text-center py-12">
        <Shield className="h-16 w-16 text-gray-400 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-gray-900 mb-2">No Setup Required</h1>
        <p className="text-gray-600 mb-6">You don't have a pending MFA setup.</p>
        <Button onClick={() => navigate('/login')}>Go to Login</Button>
      </Card>
    );
  }

  // Verify code and enable MFA
  const handleVerifyCode = async (code: string) => {
    if (!pendingUser || !enrollmentData) return;

    setVerifying(true);
    setVerificationError('');

    try {
      // Verify the code matches (client-side check for immediate feedback)
      const isValid = verifyTotpCode(enrollmentData.secret, code);

      if (!isValid) {
        setVerificationError('Invalid code. Please try again.');
        setVerifying(false);
        return;
      }

      // Enable MFA via Cloud Function (server-side)
      await enableMfa(pendingUser.uid, enrollmentData.secret, enrollmentData.backupCodes, code);

      // Force token refresh to get new claims
      await pendingUser.getIdToken(true);

      setStep('backup-codes');
      toast.success('Two-factor authentication enabled!');
    } catch (error) {
      console.error('Failed to enable MFA:', error);
      setVerificationError('Failed to enable MFA. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  // Complete setup and redirect to app
  // Note: Organization creation + category seeding is handled by AppLayout
  const handleComplete = async () => {
    if (!pendingUser) return;

    try {
      // Get current profile (may not have organizationId yet - AppLayout handles org creation)
      const profile = await getUserProfile(pendingUser.uid);

      // Mark as navigating BEFORE state update to prevent early return from showing "Go to Login"
      isNavigatingRef.current = true;

      useAuthStore.setState({
        user: pendingUser,
        profile,
        loading: false,
        mfaPending: false,
        mfaSetupRequired: false,
        pendingUser: null,
      });
      toast.success('Welcome! Your account is now secured.');
      navigate(redirectUrl);
    } catch (error) {
      console.error('Failed to complete setup:', error);
      toast.error('Failed to complete setup');
    }
  };

  // Copy backup codes
  const handleCopyBackupCodes = async () => {
    if (!enrollmentData) return;

    const codesText = enrollmentData.backupCodes.join('\n');
    await navigator.clipboard.writeText(codesText);
    setCopiedBackupCodes(true);
    toast.success('Backup codes copied to clipboard');
    setTimeout(() => setCopiedBackupCodes(false), 3000);
  };

  // Cancel and sign out
  const handleCancel = async () => {
    await cancelMfa();
    navigate('/login');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12">
      <Card className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
            <Shield className="h-8 w-8 text-primary-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Secure Your Account</h1>
          <p className="text-gray-600 mt-1">
            Two-factor authentication is required for all accounts
          </p>
        </div>

        {/* Step 1: Scan QR Code */}
        {step === 'scanning' && (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="font-medium text-gray-900 mb-2">Step 1: Scan QR Code</h2>
              <p className="text-sm text-gray-600 mb-4">
                Open your authenticator app (Google Authenticator, Authy, etc.) and scan this code
              </p>

              <div className="inline-block p-4 bg-white border rounded-lg">
                <QRCodeSVG
                  value={enrollmentData.qrCodeUrl}
                  size={192}
                  level="M"
                  includeMargin={false}
                />
              </div>

              <details className="mt-4 text-left">
                <summary className="text-sm text-primary-600 cursor-pointer hover:text-primary-700">
                  Can't scan? Enter code manually
                </summary>
                <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Secret key:</p>
                  <code className="text-sm font-mono break-all">{enrollmentData.secret}</code>
                </div>
              </details>
            </div>

            <div className="flex gap-3">
              <Button variant="secondary" onClick={handleCancel} className="flex-1">
                Cancel
              </Button>
              <Button onClick={() => setStep('verifying')} className="flex-1">
                Next
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Verify Code */}
        {step === 'verifying' && (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="font-medium text-gray-900 mb-2">Step 2: Verify Code</h2>
              <p className="text-sm text-gray-600 mb-4">
                Enter the 6-digit code from your authenticator app
              </p>

              <OtpInput
                value={verificationCode}
                onChange={setVerificationCode}
                onComplete={handleVerifyCode}
                error={verificationError}
                disabled={verifying}
              />
            </div>

            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={() => setStep('scanning')}
                disabled={verifying}
                className="flex-1"
              >
                Back
              </Button>
              <Button
                onClick={() => handleVerifyCode(verificationCode)}
                disabled={verificationCode.length !== 6 || verifying}
                loading={verifying}
                className="flex-1"
              >
                Verify
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Backup Codes */}
        {step === 'backup-codes' && (
          <div className="space-y-6">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mb-4">
                <Check className="h-6 w-6 text-green-600" />
              </div>
              <h2 className="font-medium text-gray-900 mb-2">Save Your Backup Codes</h2>
              <p className="text-sm text-gray-600 mb-4">
                Store these codes safely. Use them if you lose access to your authenticator.
              </p>
            </div>

            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="grid grid-cols-2 gap-2 font-mono text-sm">
                {enrollmentData.backupCodes.map((code, index) => (
                  <div key={index} className="p-2 bg-white rounded border text-center">
                    {code}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={handleCopyBackupCodes}
                leftIcon={copiedBackupCodes ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                className="flex-1"
              >
                {copiedBackupCodes ? 'Copied!' : 'Copy Codes'}
              </Button>
              <Button onClick={handleComplete} className="flex-1">
                Continue
              </Button>
            </div>

            <p className="text-xs text-center text-gray-500">
              Each code can only be used once.
            </p>
          </div>
        )}
      </Card>
    </div>
  );
}
